// @ts-strict-ignore
/* eslint-disable camelcase */
import { mapObj } from '@u/obj'
import { pathname, brand } from '@r/app-selectors'
import {
  findCalculatedValuesInSchema,
  generateValueCalculated,
} from '@H/elasticsearch/calculator-functions'
import { FsCore } from '@t/fs-core'
import { getCalculatorInputs } from '@r/calculators/calculators-selectors'
import getWindow from '@u/get-window'

const generateMojoPayload = (pathname: string) => {
  let purchaseType = null
  let occupancy = 'Live_in_property'
  let mortgageType = null

  switch (pathname) {
    case '/mortgages/first-time-buyer/':
      purchaseType = 'first-time-buyer'
      mortgageType = 'purchase'
      break
    case '/mortgages/moving-home/':
      purchaseType = 'home-move'
      mortgageType = 'purchase'
      break
    case '/mortgages/buy-to-let/':
      occupancy = 'Rent_it_out'
      mortgageType = 'purchase'
      break
    default:
      break
  }

  const res = {
    purchaseType,
    'basicPropertyDetails.Occupancy': occupancy,
    mortgageType,
  }

  const jsonParams = JSON.stringify(res)
  const mojoEncoded = getWindow().btoa
    ? getWindow().btoa(jsonParams)
    : Buffer.from(jsonParams).toString('base64')
  return mojoEncoded
}

export const prepareECLinkParams = (eligibility: any) => {
  return eligibility
    ? [
        {
          parameterKey: 'secure_product_token',
          // secure_product_token is a special case, it goes inside the 'r' redirect parameter,
          // so it has to first form-encode and then url encode
          value: encodeURIComponent(eligibility.secureProductToken),
          condition: (link, value) =>
            link.destination_url && link.destination_url.includes('secure_product_token') && value,
        },
        {
          parameterKey: 'external_check_id',
          value: eligibility.flowId,
          condition: (link, value) => !!value,
        },
        {
          parameterKey: 'check_provider',
          value: eligibility.provider,
          condition: (link, value) => !!value,
        },
      ]
    : []
}

export const generalLinkParams = (_id, siteBrand) => [
  {
    parameterKey: 'product_id',
    value: _id,
    condition: (link, value) => !!value,
  },
  {
    parameterKey: 'platform',
    value: `fs-core-${siteBrand}`,
    condition: (link, value) => !!value,
  },
]

export const hydrateProductLink = (link, linkParameters) => {
  if (link.destination_url) {
    const hydratedUrl = linkParameters.reduce((url, param) => {
      if (param.condition(link, param.value) && link.destination_url) {
        url.searchParams.append(param.parameterKey, param.value)
      }
      return url
    }, new URL(link.destination_url))

    return hydratedUrl.toString()
  }
}

const addPageQueryParam = (link: any, s: FsCore.State) =>
  `${link}${link.indexOf('?') > -1 ? '&' : '?'}page=${pathname(s)}`

const appendAdditionalDataToLink =
  (state: FsCore.State, calculatorId: string, source: any, eligibility: any, _id: any) =>
  (link: FsCore.ESLink) => {
    if (typeof link !== 'object' || link === null) return

    const rawLink = link.destination_url
    if (!rawLink) return link

    const calcInputResults = getCalculatorInputs(state, calculatorId)
    const replaceUrlParamaters = {
      propertyValue: calcInputResults.propertyValue,
      repaymentPeriod: calcInputResults.repaymentPeriod,
      mortgageAmount: calcInputResults.mortgageAmount,
      rateType: calcInputResults.rateType,
      currentProvider: calcInputResults.currentProvider,
      timeframeForMortgage: calcInputResults.timeframeForMortgage,
      timeframeForMortgageNatWest: calcInputResults.timeframeForMortgageNatWest,
      productName: source?.definition?.display_name,
      utm_campaign: state?.contentful?.productTable?.fields?.schema?.utmCampaign,
      seedData: generateMojoPayload(pathname(state)),
    }

    const url = addPageQueryParam(rawLink, state)
    let decodedUrl = decodeURI(url)

    Object.entries(replaceUrlParamaters).forEach(([key, value]) => {
      // eslint-disable-next-line security/detect-non-literal-regexp
      const regex = new RegExp(`{{${key}}}`)
      decodedUrl = decodedUrl.replace(regex, value)
    })

    const hydratedUrl = hydrateProductLink(
      {
        ...link,
        // eslint-disable-next-line
        destination_url: decodedUrl,
      },
      [...prepareECLinkParams(eligibility), ...generalLinkParams(_id, brand(state))],
    )

    return {
      ...link,
      // eslint-disable-next-line
      destination_url: hydratedUrl,
    }
  }

const appendAdditionalDataToLinks =
  (state: FsCore.State, calculatorId: string, source: any, eligibility: any, _id: any) =>
  (brandLinks: FsCore.ESProviderLinks = {}) =>
    mapObj(appendAdditionalDataToLink(state, calculatorId, source, eligibility, _id), brandLinks)

export const getESState = (s: FsCore.State) => s.elasticsearch

export const getESProductDataSet = (
  s: FsCore.State,
  queryId: string,
): FsCore.ESQueryState | undefined => getESState(s).productDataSet[queryId]
export const getESQuery = (s: FsCore.State, queryId: string): FsCore.ESQueryState | undefined =>
  getESState(s).queries[queryId]

export const getESData = (s: FsCore.State, queryId: string): FsCore.ESQueryData | undefined =>
  getESQuery(s, queryId)?.data

export const getESProductDataSetData = (
  s: FsCore.State,
  queryId: string,
): FsCore.ESQueryData | undefined => getESProductDataSet(s, queryId)?.data

type ProductDataSetID = string

export const hits = (s: FsCore.State, key: ProductDataSetID) =>
  (getESData(s, key) && getESData(s, key)?.hits?.hits) || []

export const sources = (s: FsCore.State, key: string) => {
  const calculatorId = getESQuery(s, key)?.calculatorId
  if (!calculatorId) {
    // eslint-disable-next-line no-console
    console.error(`No ES query state or calculator id for ${key}`)
  }

  return hits(s, key).map(({ _source = {}, eligibility, _id }) => ({
    ..._source,
    links: mapObj(
      appendAdditionalDataToLinks(s, calculatorId!, _source, eligibility, _id),
      _source.links || {},
    ),
  }))
}

export const parsedHits = (s: FsCore.State, key: string) => {
  const calculatorId = getESQuery(s, key)?.calculatorId
  if (!calculatorId) {
    // eslint-disable-next-line no-console
    console.error(`No ES query state or calculator id for ${key}`)
  }

  return hits(s, key).map((hit = {} as FsCore.ESHit) => ({
    ...hit,
    _source: {
      ...(hit._source || {}),
      links: mapObj(
        appendAdditionalDataToLinks(s, calculatorId!, hit._source, hit.eligibility, hit._id),
        hit._source?.links || {},
      ),
    },
  }))
}

/** Generates the list of parsed ES hits, with new added fields
 * coming from our own code (currently only calculated values).
 * This allows product tables to display programmatically generated fields.
 */
export const parsedHitsWithExtraData = (
  state: FsCore.State,
  calculatorInputs: FsCore.CalculatorInputs | undefined,
  schema: any,
  key: string,
) => {
  const hits = parsedHits(state, key)
  if (!calculatorInputs) {
    return hits
  }
  const calculatedValues = findCalculatedValuesInSchema(schema)
  for (const hit of hits) {
    if (calculatedValues.length) {
      hit.calculatedValue = {}
      for (const functionName of calculatedValues) {
        hit.calculatedValue[functionName] = generateValueCalculated(
          functionName,
          calculatorInputs,
          hit,
          hit.calculatedValue,
        )
      }
    }
  }
  return hits
}

export const definitions = (s: FsCore.State, key: string) =>
  sources(s, key).map((source) => source?.definition)

export const product = (s: FsCore.State) => getESState(s).product

export const visualFilterQueryId = (s: FsCore.State) => getESState(s).visualFilterQuery
export const isLoading = (s: FsCore.State) => getESState(s).isLoading
export const isErrored = (s: FsCore.State) => getESState(s).isErrored
export const isQueryLoading = (s: FsCore.State, key: string) => getESQuery(s, key)?.isLoading
export const isQueryErrored = (s: FsCore.State, key: string) => getESQuery(s, key)?.isErrored
